import type { CategoryTreeNode, ResolvedCategory } from '~/stores/category-tree'

interface LegacyCategory {
  group?: string
  name?: string
  child?: string
}

/**
 * Encapsulates the `click_product_category` GA4 tracker so call-sites only
 * have to compose the category hierarchy. All emitted labels are forced to
 * the country-configured analytics locale (defaults to English).
 */
export function useCategoryClickTracking() {
  const gtm = useGtm()
  const { t, locale } = useI18n()
  const treeStore = useCategoryTreeStore()
  const { searchLanguage, gtmLanguage } = useCountryConfig()

  function nodeLabel(node: CategoryTreeNode): string {
    return node.terms[gtmLanguage] ?? node.terms[locale.value] ?? ''
  }

  function keyLabel(i18nKey: string): string {
    return t(i18nKey, {}, { locale: gtmLanguage })
  }

  function levelsFromResolved(resolved: ResolvedCategory): string[] {
    return [...resolved.ancestors, resolved.node].map(nodeLabel)
  }

  function levelsFromCategoryIds(categoryIds: string[]): string[] {
    if (categoryIds.length === 0 || treeStore.roots.length === 0) return []
    const resolved = treeStore.resolveDeepestByIds(categoryIds)
    return resolved ? levelsFromResolved(resolved) : []
  }

  /**
   * Walks the category tree to resolve `categoriesNew` (terms in
   * `searchLanguage`) into an analytics-locale level array.
   */
  function levelsFromCategoriesNew(categoriesNew: string[]): string[] {
    if (categoriesNew.length === 0 || treeStore.roots.length === 0) return []
    const resolved = treeStore.resolveDeepestPath(categoriesNew, searchLanguage)
    return resolved ? levelsFromResolved(resolved) : []
  }

  /**
   * Translates the legacy product category (i18n keys for group/name/child)
   * into an analytics-locale level array.
   */
  function levelsFromLegacyCategory(category: LegacyCategory | null | undefined): string[] {
    if (!category?.group || !category?.name) return []
    const second = category.group === 'enum.product_category_group.food' && category.child
      ? category.child
      : category.name
    return [keyLabel(category.group), keyLabel(second)]
  }

  function track(levels: Array<string | undefined | null>, pageSection: string) {
    const cleaned = levels
      .map(level => (typeof level === 'string' ? level.trim() : ''))
      .filter((level): level is string => level.length > 0)
    const clicked = cleaned.at(-1)
    if (!clicked) return

    gtm?.trackEvent({
      event: 'click_product_category',
      category_name: clicked,
      page_section: pageSection,
      ...buildGa4ItemCategoryParams(cleaned),
    })
  }

  return {
    track,
    nodeLabel,
    levelsFromResolved,
    levelsFromCategoryIds,
    levelsFromCategoriesNew,
    levelsFromLegacyCategory,
  }
}
